import { Injectable } from "@angular/core";
import { ShapeResourceManagerService } from "../../shared/helpers/shape-resource-manager.service";
import { MeshType } from "~/src/utils/shape-type";
import { Face, FaceType } from "~/src/utils/count-calculator/count-calculator.model";
import { ColorRepresentation, MeshBasicMaterial, DoubleSide, Mesh } from "three";

// Material configuration for each mesh type
interface MaterialConfig {
    materialNames: string[];
}

@Injectable({
    providedIn: "root",
})
export class ShapeGeneratorService {
    constructor(private shapeResourceManagerService: ShapeResourceManagerService) {}

    private readonly MATERIAL_CONFIGS: Record<MeshType, MaterialConfig> = {
        [MeshType.Cube]: {
            materialNames: [Face.Front, Face.Back, Face.Top, Face.Bottom, Face.Left, Face.Right],
        },
        [MeshType.CurvedCube]: {
            materialNames: [FaceType.Curved, Face.Back, Face.Bottom, Face.Left, Face.Right],
        },
        [MeshType.CurvedCube2]: {
            materialNames: [Face.Front, Face.Back, FaceType.Curved, Face.Bottom],
        },
        [MeshType.HalfCylinder]: {
            materialNames: [FaceType.Curved, Face.Bottom, Face.Left, Face.Right],
        },
        [MeshType.Prism]: {
            materialNames: [Face.Front, Face.Back, Face.Bottom, Face.Left, Face.Right],
        },
        [MeshType.SquarePyramid]: {
            materialNames: [Face.Front, Face.Back, Face.Bottom, Face.Left, Face.Right],
        },
        [MeshType.Cone]: {
            materialNames: [FaceType.Curved, Face.Bottom],
        },
        [MeshType.Cylinder]: {
            materialNames: [FaceType.Curved, Face.Top, Face.Bottom],
        },
        [MeshType.HemiSphere]: {
            materialNames: [FaceType.Curved, Face.Bottom],
        },
        [MeshType.RightTriangle]: {
            materialNames: [Face.Front, Face.Back, Face.Bottom, Face.Left, Face.Right],
        },
        // [MeshType.ConcavePrism]: {
        //     materialNames: [FaceType.Curved, Face.Back, Face.Bottom, Face.Left, Face.Right]
        // }
    };

    /**
     * Creates materials for a mesh based on its type
     */
    public createMaterials(
        type: MeshType,
        color: ColorRepresentation,
        isForSelector: boolean,
    ): MeshBasicMaterial | MeshBasicMaterial[] {
        if (isForSelector) {
            return new MeshBasicMaterial({ color });
        }

        const config = this.MATERIAL_CONFIGS[type];
        if (!config) {
            throw new Error(`No material configuration found for mesh type: ${type}`);
        }

        return config.materialNames.map(
            (name) =>
                new MeshBasicMaterial({
                    color,
                    side: DoubleSide,
                    name,
                }),
        );
    }

    /**
     * Generic method which creates the shape for all types
     */
    public generateShape(type: MeshType, color: ColorRepresentation, isForSelector: boolean): Mesh {
        const geometry = this.shapeResourceManagerService.getGeometry(type, isForSelector);
        const materials = this.createMaterials(type, color, isForSelector);

        const shape = new Mesh(geometry, materials);
        shape.name = type;

        this.shapeResourceManagerService.addOutlineToMesh(shape, isForSelector);

        return shape;
    }
}
